/**
 * ImportPreviewStep — Schritt 4 des Import-Wizards: Vorschau + Duplikat-
 * Zähler + Import-Auslösung (architecture-bank-reconciliation.md §4 Schritt 6/7).
 *
 * Zeigt die ersten 20 normalisierten Zeilen, die Anzahl bereits importierter
 * Duplikate (Batch-Existenz-Check über `findExistingTxIds`, §6.4) und
 * Zeilen-Fehler (`parsed.errors`). Der eigentliche Import (Storage-Upload +
 * `writeBatch`-Persistenz) läuft in `bankApi.importStatement` — dieser
 * Schritt ruft nur `onImport()` auf, den Rest orchestriert `ImportWizard`.
 *
 * @module ImportPreviewStep
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconWarning, IconSuccess } from '../../icons';
import { ClipLoader } from 'react-spinners';
import { findExistingTxIds } from '../../api/bankApi';
import { computeTxDocId, assignOccurrenceIndexes } from '../../utils/bankImport/dedup';
import type { ParsedStatement } from '../../utils/bankImport/types';
import type { BankStatementImport, StatementFileFormat } from '../../types/bankTypes';
import { centsToEuros } from '../../utils/moneyUtils';
import { formatDate as formatDateCentral } from '../../utils/formatters';
import logger from '../../utils/logger';
import { mapErrorToUserMessage } from '../../utils/userErrorMessage';

/** Explicit user-confirmed coverage period for CSV imports (MA-2, §4.6/§6.3). */
export interface CsvCoverageConfirmation {
  /** ISO `YYYY-MM-DD`. */
  coverageFrom: string;
  /** ISO `YYYY-MM-DD`. */
  coverageTo: string;
}

export interface ImportPreviewStepProps {
  parsed: ParsedStatement;
  accountId: string;
  /**
   * MA-2: CAMT-Dateien tragen ihre Auszugsperiode bereits in `parsed.coverageFrom/To`
   * (aus `Stmt/FrToDt`); nur für CSV-Importe zeigt dieser Schritt das optionale
   * Bestätigungsfeld „Export deckt lückenlos ab von/bis" (architecture-mahn-
   * automatik.md §6.3, CSV kennt keinen äquivalenten Header).
   */
  fileFormat: StatementFileFormat;
  onBack: () => void;
  /**
   * MA-2: bei CSV-Importen mit ausgefüllter Bestätigung wird das Zeitraum-Paar
   * mitgegeben — `ImportWizard` merged es auf `parsed` bevor `importStatement`
   * aufgerufen wird. `undefined` (Feld leer gelassen) ⇒ kein Override, Gate
   * fällt auf `statementFrom/To` zurück (§6.3, fail-closed-sicher).
   */
  onImport: (csvCoverage?: CsvCoverageConfirmation) => void | Promise<void>;
  loading: boolean;
  error: string | null;
  result: BankStatementImport | null;
  onClose: () => void;
}

const PREVIEW_ROW_LIMIT = 20;

const ImportPreviewStep: React.FC<ImportPreviewStepProps> = ({
  parsed,
  accountId,
  fileFormat,
  onBack,
  onImport,
  loading,
  error,
  result,
  onClose,
}) => {
  const { t, i18n } = useTranslation('bank');
  const [duplicateCount, setDuplicateCount] = useState<number | null>(null);
  const [dupCheckError, setDupCheckError] = useState<string | null>(null);
  // MA-2 (§6.3): nur für CSV relevant — CAMT bekommt seine Periode automatisch
  // aus parsed.coverageFrom/To (Stmt/FrToDt).
  const [csvCoverageFrom, setCsvCoverageFrom] = useState('');
  const [csvCoverageTo, setCsvCoverageTo] = useState('');

  const rowsWithIds = useMemo(() => {
    const indexed = assignOccurrenceIndexes(parsed.rows);
    return indexed.map(({ row, occurrenceIndex }) => ({
      row,
      txId: computeTxDocId(accountId, row, occurrenceIndex),
    }));
  }, [parsed.rows, accountId]);

  useEffect(() => {
    let cancelled = false;
    setDuplicateCount(null);
    setDupCheckError(null);
    findExistingTxIds(rowsWithIds.map((r) => r.txId))
      .then((existing) => {
        if (!cancelled) setDuplicateCount(existing.size);
      })
      .catch((err: any) => {
        logger.error(err, 'duplicate tx check failed');
        if (!cancelled) {
          setDupCheckError(mapErrorToUserMessage(err, t, { fallbackKey: 'wizard.preview.dupCheckFailed' }));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [rowsWithIds, t]);

  const formatDate = (iso: string) => formatDateCentral(iso, { locale: i18n.language });

  const formatAmount = (cents: number) =>
    centsToEuros(cents).toLocaleString(i18n.language || 'de-DE', {
      style: 'currency',
      currency: 'EUR',
    });

  if (result) {
    return (
      <div className="space-y-5 text-center py-6">
        <IconSuccess size={36} className="mx-auto text-[var(--tone-success-fg)]" />
        <p className="text-primary font-medium">{t('wizard.preview.success', 'Import abgeschlossen')}</p>
        <div className="flex justify-center gap-6 text-sm text-secondary">
          <span>{t('wizard.preview.statImported', '{{count}} importiert', { count: result.stats.imported })}</span>
          <span>{t('wizard.preview.statDuplicates', '{{count}} Duplikate', { count: result.stats.duplicates })}</span>
          {result.stats.errors > 0 && (
            <span className="text-[var(--tone-warning-fg)]">
              {t('wizard.preview.statErrors', '{{count}} Fehlerzeilen', { count: result.stats.errors })}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="px-5 py-2 bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-medium rounded-lg"
        >
          {t('wizard.close', 'Schließen')}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-4 text-sm">
        <span className="text-secondary">
          {t('wizard.preview.totalRows', '{{count}} Zeilen', { count: parsed.rows.length })}
        </span>
        <span className="text-[var(--tone-warning-fg)]">
          {duplicateCount === null
            ? t('wizard.preview.checkingDuplicates', 'Duplikate werden geprüft…')
            : t('wizard.preview.duplicates', '{{count}} bereits importiert (werden übersprungen)', {
                count: duplicateCount,
              })}
        </span>
        {parsed.errors.length > 0 && (
          <span className="text-[var(--tone-danger-fg)]">
            {t('wizard.preview.errorRows', '{{count}} Zeilen konnten nicht gelesen werden', {
              count: parsed.errors.length,
            })}
          </span>
        )}
      </div>

      {dupCheckError && (
        <div className="bg-[var(--tone-warning-bg)] border border-[var(--tone-warning-border)] rounded-lg p-3 text-sm text-[var(--tone-warning-fg)] flex items-start gap-2">
          <IconWarning className="mt-0.5 flex-shrink-0" />
          <span>{dupCheckError}</span>
        </div>
      )}

      <div className="border border-hairline rounded-lg overflow-hidden">
        <div className="overflow-x-auto max-h-72">
          <table className="w-full text-xs">
            <thead className="bg-surface-raised text-muted sticky top-0">
              <tr>
                <th className="text-left px-3 py-2">{t('wizard.preview.col.date', 'Datum')}</th>
                <th className="text-right px-3 py-2">{t('wizard.preview.col.amount', 'Betrag')}</th>
                <th className="text-left px-3 py-2">{t('wizard.preview.col.counterparty', 'Gegenpartei')}</th>
                <th className="text-left px-3 py-2">{t('wizard.preview.col.purpose', 'Verwendungszweck')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[color:var(--border-hairline)]">
              {rowsWithIds.slice(0, PREVIEW_ROW_LIMIT).map(({ row, txId }) => (
                <tr key={txId} className="text-secondary">
                  <td className="px-3 py-1.5 whitespace-nowrap">{formatDate(row.bookingDate)}</td>
                  <td
                    className={`px-3 py-1.5 text-right whitespace-nowrap ${
                      row.amountCents < 0 ? 'text-[var(--tone-danger-fg)]' : 'text-[var(--tone-success-fg)]'
                    }`}
                  >
                    {formatAmount(row.amountCents)}
                  </td>
                  <td className="px-3 py-1.5 max-w-[160px] truncate">{row.counterpartyName || '—'}</td>
                  <td className="px-3 py-1.5 max-w-[220px] truncate">{row.purpose || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {parsed.rows.length > PREVIEW_ROW_LIMIT && (
          <div className="bg-[rgb(var(--surface-raised-rgb)/0.6)] px-3 py-1.5 text-xs text-faint">
            {t('wizard.preview.moreRows', '… und {{count}} weitere Zeilen', {
              count: parsed.rows.length - PREVIEW_ROW_LIMIT,
            })}
          </div>
        )}
      </div>

      {error && (
        <div className="bg-[var(--tone-danger-bg)] border border-[var(--tone-danger-border)] rounded-lg p-3 text-sm text-[var(--tone-danger-fg)]">{error}</div>
      )}

      {fileFormat === 'csv' && (
        <div className="border border-hairline rounded-lg p-3 space-y-2 bg-surface-raised">
          <p className="text-xs text-secondary">
            {t(
              'wizard.preview.coverageConfirm.hint',
              'Optional: Bestätige den lückenlosen Zeitraum, den dieser Export abdeckt (relevant für die Mahn-Automatik).'
            )}
          </p>
          <div className="flex flex-wrap items-center gap-3 text-xs">
            <label className="flex items-center gap-1.5 text-muted">
              {t('wizard.preview.coverageConfirm.from', 'Von')}
              <input
                type="date"
                className="bg-surface border border-hairline rounded px-2 py-1 text-primary"
                value={csvCoverageFrom}
                onChange={(e) => setCsvCoverageFrom(e.target.value)}
              />
            </label>
            <label className="flex items-center gap-1.5 text-muted">
              {t('wizard.preview.coverageConfirm.to', 'Bis')}
              <input
                type="date"
                className="bg-surface border border-hairline rounded px-2 py-1 text-primary"
                value={csvCoverageTo}
                onChange={(e) => setCsvCoverageTo(e.target.value)}
              />
            </label>
          </div>
        </div>
      )}

      <div className="flex justify-between pt-2">
        <button type="button" onClick={onBack} disabled={loading} className="px-4 py-2 text-sm text-muted hover:text-primary disabled:opacity-50">
          {t('wizard.back', 'Zurück')}
        </button>
        <button
          type="button"
          onClick={() =>
            onImport(
              fileFormat === 'csv' && csvCoverageFrom && csvCoverageTo
                ? { coverageFrom: csvCoverageFrom, coverageTo: csvCoverageTo }
                : undefined
            )
          }
          disabled={loading || duplicateCount === null}
          className="px-5 py-2 bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-medium rounded-lg disabled:opacity-50 flex items-center gap-2"
        >
          {loading && <ClipLoader color="#fff" size={14} />}
          {t('wizard.preview.importButton', 'Importieren')}
        </button>
      </div>
    </div>
  );
};

export default ImportPreviewStep;
